import axios from "axios";
import React, {useEffect, useState, useRef, useMemo, useCallback, useActionState} from "react";
import {ErrorMessage, Field, Form, Formik} from "formik";
import * as Yup from "yup";
import {useNavigate} from "react-router-dom";
import Successwithfeedbackmodal from "../shared/Successwithfeedbackmodal";
import FailureModal from "../shared/FailureModal";
import ProfileModal from "../shared/ProfileModal";
import JoditEditorComponent from "../shared/JoditEditorComponent"
import CitySearchDropdown from "../Dropdowns/CitySearchDropdown";
import SkillsSearchDropdown from "../Dropdowns/SkillsSearchDropdown";

const PostAjobCard = ({job, updateMode = false}) => {
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showFailureModal, setShowFailureModal] = useState(false);
  const [showSuccessCreationModal, setShowSuccessCreationModal] = useState(false);
  const [showFailureCreationModal, setShowFailureCreationModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [showProfileModal, setShowProfileModal] = useState(false);

  const formatDate = useCallback((sqlDate) => {
    const date = new Date(sqlDate);
    if (date && !isNaN(new Date(date))) {
      return date.toISOString().split('T')[0];
  } else {
      console.error("Invalid date value:", date);
  }
  }, []);

  const initialValues = useMemo(() => {
    if (!updateMode) {
      return {
        job_name: '',
        job_type: '',
        number_of_positions: '',
        salary: '',
        experience: '',
        qualification: '',
        skills: [],
        hr_name: '',
        department: '',
        city: '',
        post_date: '',
        description: '',
        job_external_url: "",
        job_application_internal: "",
        highlights: "",
        end_date: '',
        work_mode: ''
      };
    }

    return {
      job_name: job?.job_name || '',
      job_type: job?.job_type || '',
      number_of_positions: job?.number_of_positions || '',
      salary: job?.package || '',
      experience: job?.experience || '',
      qualification: job?.qualification || '',
      skills: job?.skills || [],
      hr_name: job?.hr_name || '',
      department: job?.department || '',
      city: job?.city || '',
      post_date: formatDate(job?.post_date),
      description: job?.job_description || '',
      job_external_url: job?.job_external_url || '',
      job_internal_application: job?.job_internal_application || '',
      highlights: job?.job_highlights || '',
      end_date: formatDate(job?.end_date),
      work_mode: job?.work_mode || ''
    };
  }, [job, updateMode, formatDate]);


  const validationSchema = useMemo(() => Yup.object({
    city: Yup.string().required('Address is required'),
    department: Yup.string(),
    description: Yup.string().required('Description is required'),
    end_date: Yup.date().required('End date is required'),
    experience: Yup.string(),
    job_name: Yup.string().required('Job title is required'),
    job_type: Yup.string().required('Job type is required'),
    number_of_positions: Yup.number()
      .required('Number of positions is required')
      .positive('Must be a positive number')
      .integer('Must be a whole number'),
    post_date: Yup.date().required('Start date is required'),
    qualification: Yup.string().required('Qualification is required'),
    salary: Yup.string(),
    skills: Yup.array()
      .of(
        Yup.object().shape({
          skill_id: Yup.string().required(),
          skill_name: Yup.string().required()
        })
      )
      .min(1, 'At least one skill is required'),
    work_mode: Yup.string().required('Work mode is required'),
  }));

  const [skills, setSkills] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [degrees, setDegrees] = useState([]);
  const [hrs, setHrs] = useState([]);

  const checkCompanyProfile = async () => {
    try {
      const companyId = localStorage.getItem("companyId");
      if (!companyId) {
        setShowProfileModal(true);
        return false;
      }
      
      const response = await axios.get(`${process.env.REACT_APP_API_URL}companyDetails/bycompanyid/${companyId}`);
      
      const company = response.data[0];
      if (!company ||
        !company.company_name ||
        !company.primary_industry ||
        !company.company_website) {
        setShowProfileModal(true);
        return false;
      }
      return true;
    } catch (error) {
      console.log("Error checking company profile:", error);
      setShowProfileModal(true);
      return false;
    }
  };

  const fetchAllSkills = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}skills/showallskills`);
      setSkills(response.data);
    } catch (error) {
      console.log(error);
    }
  }

  const fetchAllDepartments = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}department/getdepartments`);
      setDepartments(response.data)
    } catch (error) {
      console.log(error);
    }
  }
  const fetchAllDegrees = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}degree/getdegrees`);
      setDegrees(response.data)
    } catch (error) {
      console.log(error);
    }
  }

  const fetchAllHrs = async () => {
    try {
      const companyId = localStorage.getItem("companyId");
      const response = await axios.get(`${process.env.REACT_APP_API_URL}hr_details/showallhr_details/${companyId}`);
      setHrs(response.data);
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    checkCompanyProfile();
    fetchAllSkills();
    fetchAllDepartments();
    fetchAllDegrees();
    fetchAllHrs();
  }, []);

  const navigate = useNavigate();
  const updateJob = async (values, {resetForm}) => {
    const isProfileComplete = await checkCompanyProfile();
    if (!isProfileComplete) {
      return;
    }

    try {
      const response = await axios.put(`${process.env.REACT_APP_API_URL}jobs/update-job/${job.job_id}`, {...values});
      if (response.status === 200) {
        setShowSuccessModal(true);
      } else {
        console.log("Error on updating");
        setShowFailureModal(true);
      }
    } catch (error) {
      console.log(error);
      setShowFailureModal(true);
    }
  }

  const handleSubmit = async (values, {resetForm}) => {
    const isProfileComplete = await checkCompanyProfile();
    if (!isProfileComplete) {
      return;
    }
    const userid = localStorage.getItem("userid");
    const companyId = localStorage.getItem("companyId");
    if (!userid) {
      alert("User ID not found. Please log in again.");
      return;
    }

    if (!companyId) {
      alert("Company ID not found. Please log in again.");
      return;
    }

    const postData = {...values, userid, company_id: companyId};

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}jobs/post-job`,
        postData,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        setSuccessMessage(`Job posted successfully! Job ID: ${response.data.jobId}`);
        setShowSuccessCreationModal(true);
      }
    } catch (error) {
      console.error("Error posting job:", error);
      setShowFailureCreationModal(true);
    }
  };
  const handleProfileRedirect = () => {
    const companyId = localStorage.getItem("companyId");
    if (companyId) {
      navigate('/company-profile');
    }
  };


  return (
    <div className="col-lg-9">
      <div className="user-profile-wrapper">
        <div className="user-profile-card">
          <h4 className="user-profile-card-title">Post Job</h4>
          <div className="profile-resume">
            <div className="profile-form">
              <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={async (values, {resetForm}) => {
                  updateMode ? await updateJob(values, {resetForm}) : await handleSubmit(values, {resetForm});
                }}
              >
                {({isSubmitting}) => (<Form className="row">
                    <div className="col-md-12">
                      <div className="form-group">
                        <label htmlFor="job_name">Job Title</label><span className="text-danger">*</span>
                        <Field
                          type="text"
                          name="job_name"
                          className="form-control"
                          placeholder="Enter Job Title"
                        />
                        <ErrorMessage name="job_name" component="div" className="text-danger"/>
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className="form-group">
                        <label htmlFor="work_mode">Work Mode</label><span className="text-danger">*</span>
                        <Field
                          as="select"
                          name="work_mode"
                          className="form-control"
                        >
                          <option value="">--Choose Work Mode--</option>
                          <option value="wfh">Work From Home</option>
                          <option value="hybrid">Hybrid</option>
                          <option value="wfo">Work From Office</option>
                        </Field>
                        <ErrorMessage name="work_mode" component="div" className="text-danger"/>
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className="form-group">
                        <label htmlFor="job_type">Job Type</label><span className="text-danger">*</span>
                        <Field
                          as="select"
                          name="job_type"
                          className="form-control"
                        >
                          <option value="">Choose Type</option>
                          <option value="Full Time">Full Time</option>
                          <option value="Part Time">Part Time</option>
                          <option value="Remote">Remote</option>
                          <option value="Freelancer">Freelancer</option>
                          <option value="Temporary">Temporary</option>
                          <option value="Internship">Internship</option>
                        </Field>
                        <ErrorMessage name="job_type" component="div" className="text-danger"/>
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className="form-group">
                        <label htmlFor="number_of_positions">No. of Position</label><span
                        className="text-danger">*</span>
                        <Field
                          type="number"
                          name="number_of_positions"
                          className="form-control"
                          placeholder="Enter total positions"
                        />
                        <ErrorMessage name="number_of_positions" component="div" className="text-danger"/>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="form-group">
                        <label htmlFor="salary">Offered Salary</label>
                        <Field
                          type="text"
                          name="salary"
                          className="form-control"
                          placeholder="Enter Offered Salary (e.g. 4 LPA, Not Disclosed)"
                        />
                        <ErrorMessage name="salary" component="div" className="text-danger"/>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="form-group">
                        <label htmlFor="experience">Experience</label>
                        <Field
                          type="text"
                          name="experience"
                          className="form-control"
                          placeholder="Enter Experience (e.g. 4 - 5 Years)"
                        />
                        <ErrorMessage name="experience" component="div" className="text-danger"/>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="form-group">
                        <label htmlFor="qualification">Qualification</label><span className="text-danger">*</span>
                        <Field
                          as="select"
                          name="qualification"
                          className="form-control"
                        >
                          <option value="">Choose Highest Qualification</option>
                          {degrees.map((degree) => (
                            <option value={degree.degree_name} key={degree.d_id}>{degree.degree_name}</option>
                          ))}
                        </Field>
                        <ErrorMessage name="qualification" component="div" className="text-danger"/>
                      </div>
                      
                    </div>
                  <div className="col-md-12">
                    <div className="form-group">
                      <label htmlFor="skills">Required Skills</label><span className="text-danger">*</span>
                      <Field name="skills">
                        {({ field, form }) => {
                          let initialSkills = [];
                          try {
                            if (typeof job?.skills === 'string') {
                              initialSkills = JSON.parse(job.skills);
                            } else if (Array.isArray(job?.skills)) {
                              initialSkills = job.skills;
                            }
                          } catch (error) {
                            console.warn("Invalid JSON format in job.skills", error);
                          }

                          return (
                            <SkillsSearchDropdown
                              value={initialSkills}
                              onChange={(skills) => form.setFieldValue('skills', skills)}
                              onBlur={field.onBlur}
                              skillsList={skills}
                            />
                          );
                        }}
                      </Field>
                      <ErrorMessage name="skills" component="div" className="text-danger" />
                    </div>
                    </div>

                    <div className="col-md-4">
                      <div className="form-group">
                        <label htmlFor="hr_name">HR Name</label>
                        <Field
                          as="select"
                          name="hr_name"
                          className="form-control"
                          placeholder="Enter HR Name"
                        >
                          <option value="">Choose HR Name</option>
                          {hrs.map((hr) => (
                            <option value={hr.name} key={hr.userid}>{hr.name}</option>
                          ))}
                        </Field>
                        <ErrorMessage name="hr_name" component="div" className="text-danger"/>
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className="form-group">
                        <label htmlFor="post_date">Start Date</label><span className="text-danger">*</span>
                        <Field
                          type="date"
                          name="post_date"
                          className="form-control"
                        />
                        <ErrorMessage name="post_date" component="div" className="text-danger"/>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-group">
                        <label htmlFor="end_date">End Date</label><span className="text-danger">*</span>
                        <Field
                          type="date"
                          name="end_date"
                          className="form-control"
                        />
                        <ErrorMessage name="end_date" component="div" className="text-danger"/>
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className="form-group">
                        <label htmlFor="department">Department</label>
                        <Field
                          as="select"
                          name="department"
                          className="form-control"
                          placeholder="Enter Department"
                        >
                          <option value="">--Choose Department--</option>
                          {departments.map((department) => (
                            <option key={department.department_id}
                                    value={department.department_name}>{department.department_name}</option>
                          ))}
                        </Field>
                        <ErrorMessage name="department" component="div" className="text-danger"/>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="form-group">
                        <label htmlFor="job_external_url">Job External Application</label>
                        <Field
                          type="text"
                          name="job_external_url"
                          className="form-control"
                          placeholder="Enter Job External URL"
                        />
                        <ErrorMessage name="job_external_url" component="div" className="text-danger"/>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="form-group">
                        <label htmlFor="city">Location (City / Remote)</label><span className="text-danger">*</span>
                        <Field
                          type="text"
                          name="city"
                          className="form-control"
                          placeholder="Full Address"
                        >
                          {({field, form}) => (
                            <CitySearchDropdown name={field.name} initialValue={initialValues.city} />
                          )}
                        </Field>
                        {/* <ErrorMessage name="city" component="div" className="text-danger"/> */}
                      </div>
                    </div>

                    <div className="col-md-12">
                      <div className="form-group">
                        <label htmlFor="description">Description</label><span className="text-danger">*</span>
                        <Field
                          name="description"
                          className="form-control"
                          component={JoditEditorComponent}
                          type="text"
                        />
                        <ErrorMessage name="description" component="div" className="text-danger"/>
                      </div>
                    </div>

                    <div className="col-md-12">
                      <div className="form-group">
                        <label htmlFor="highlights">Job Highlights</label>
                        <Field
                          name="highlights"
                          className="form-control"
                          component={JoditEditorComponent}
                          type="text"
                        />
                        <ErrorMessage name="highlights" component="div" className="text-danger"/>
                      </div>
                    </div>

                    <div className="col-md-12 text-right">
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={isSubmitting}
                      >
                        {updateMode ? isSubmitting ? 'Updating...' : 'Update Job' : isSubmitting ? "Posting..." : "Post Job"}
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>

              {/* Success Modal */}
              <Successwithfeedbackmodal
                show={showSuccessModal}
                onClose={() => {
                  setShowSuccessCreationModal(false);
                  navigate("/managejobs"); // Navigate after closing the modal
                }}
                message="Job Updated Successfully!"
              />
              <FailureModal
                show={showFailureModal}
                onClose={() => setShowFailureModal(false)}
                message="Failed to Update the Job"
              />
              <ProfileModal
                show={showProfileModal}
                onClose={() => {
                  setShowProfileModal(false);
                  navigate('/employerdashboard');
                }
                }
                onComplete={handleProfileRedirect}
                message="Please complete your company profile before posting a job"
                hideCancel={true}
              />

              <>
                {showSuccessCreationModal && (
                  <Successwithfeedbackmodal
                    show={showSuccessCreationModal}
                    onClose={() => {
                      setShowSuccessCreationModal(false);
                      navigate('/managejobs')
                    }}
                    message={successMessage}
                  />
                )}
                {showFailureCreationModal && (
                  <FailureModal
                    show={showFailureCreationModal}
                    onClose={() => setShowFailureCreationModal(false)}
                    message="Failed to Create the Job"
                  />
                )}
              </>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(PostAjobCard);
